import { useEffect, useState } from "react";
import { Product } from "@/mocks/products";
import ReviewWriteModal from "@/components/base/ReviewWriteModal";

interface ProductDetailModalProps {
  product: Product | null;
  onClose: () => void;
}

const badgeColors: Record<string, string> = {
  NEW: "bg-emerald-100 text-emerald-700",
  SALE: "bg-rose-100 text-rose-600",
  BEST: "bg-amber-100 text-amber-700",
};

const KAKAO_URL = "https://open.kakao.com/o/srLtta1b";

const featureMap: Record<string, { icon: string; label: string }[]> = {
  "1": [
    { icon: "ri-fingerprint-line", label: "지문인식" },
    { icon: "ri-lock-password-line", label: "비밀번호" },
    { icon: "ri-bank-card-line", label: "카드키" },
    { icon: "ri-smartphone-line", label: "앱 제어" },
  ],
  "2": [
    { icon: "ri-fingerprint-line", label: "지문인식" },
    { icon: "ri-lock-password-line", label: "비밀번호" },
    { icon: "ri-bank-card-line", label: "카드키" },
  ],
  "3": [
    { icon: "ri-lock-password-line", label: "번호키" },
    { icon: "ri-bank-card-line", label: "카드키" },
  ],
  "4": [
    { icon: "ri-computer-line", label: "터치스크린" },
    { icon: "ri-fingerprint-line", label: "지문인식" },
    { icon: "ri-smartphone-line", label: "앱 연동" },
  ],
  "5": [
    { icon: "ri-lock-password-line", label: "번호키" },
    { icon: "ri-shield-check-line", label: "내구성 강화" },
  ],
  "6": [
    { icon: "ri-lock-password-line", label: "버튼식 번호키" },
    { icon: "ri-home-line", label: "가정용" },
  ],
};

const specMap: Record<string, { label: string; value: string }[]> = {
  "1": [
    { label: "인증 방식", value: "지문 / 비밀번호 / 카드 / 앱" },
    { label: "연결", value: "블루투스 + Wi-Fi" },
    { label: "배터리", value: "AA 4개 (약 12개월)" },
    { label: "방수 등급", value: "IP54" },
    { label: "보증 기간", value: "1년" },
  ],
  "2": [
    { label: "인증 방식", value: "지문 / 비밀번호 / 카드" },
    { label: "연결", value: "블루투스" },
    { label: "배터리", value: "AA 4개 (약 12개월)" },
    { label: "방수 등급", value: "IP44" },
    { label: "보증 기간", value: "1년" },
  ],
  "3": [
    { label: "인증 방식", value: "비밀번호 / 카드" },
    { label: "연결", value: "없음" },
    { label: "배터리", value: "AA 4개 (약 12개월)" },
    { label: "방수 등급", value: "IP44" },
    { label: "보증 기간", value: "1년" },
  ],
  "4": [
    { label: "인증 방식", value: "터치스크린 / 지문" },
    { label: "연결", value: "블루투스 + Wi-Fi" },
    { label: "배터리", value: "AA 4개 (약 10개월)" },
    { label: "방수 등급", value: "IP54" },
    { label: "보증 기간", value: "1년" },
  ],
  "5": [
    { label: "인증 방식", value: "비밀번호" },
    { label: "연결", value: "없음" },
    { label: "배터리", value: "AA 4개 (약 14개월)" },
    { label: "방수 등급", value: "IP44" },
    { label: "보증 기간", value: "1년" },
  ],
  "6": [
    { label: "인증 방식", value: "버튼식 번호키" },
    { label: "연결", value: "없음" },
    { label: "배터리", value: "AA 4개 (약 14개월)" },
    { label: "방수 등급", value: "IP44" },
    { label: "보증 기간", value: "1년" },
  ],
};

const ProductDetailModal = ({ product, onClose }: ProductDetailModalProps) => {
  const [showReview, setShowReview] = useState(false);

  useEffect(() => {
    if (product) {
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "";
    };
  }, [product]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  if (!product) return null;

  const features = featureMap[product.id] || [];
  const specs = specMap[product.id] || [];

  const handleInquiry = () => {
    window.open(KAKAO_URL, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ backgroundColor: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
    >
      <div
        className="bg-white rounded-2xl w-full max-w-3xl max-h-[90vh] overflow-y-auto animate-fadeInUp"
        onClick={(e) => e.stopPropagation()}
        style={{ animation: "fadeInUp 0.25s ease" }}
      >
        {/* Close Button */}
        <div className="flex justify-end p-4 pb-0">
          <button
            onClick={onClose}
            className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-stone-100 transition-colors cursor-pointer text-stone-500"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="flex flex-col md:flex-row gap-0">
          {/* Left: Image */}
          <div className="md:w-2/5 flex-shrink-0 px-6 pb-4 md:pb-6">
            <div className="relative rounded-xl overflow-hidden bg-stone-50" style={{ aspectRatio: "5/6" }}>
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover object-top"
              />
              {product.badge && (
                <span className={`absolute top-3 left-3 text-[10px] font-bold tracking-widest px-2.5 py-1 rounded-full ${badgeColors[product.badge] || "bg-stone-100 text-stone-600"}`}>
                  {product.badge}
                </span>
              )}
            </div>
          </div>

          {/* Right: Info */}
          <div className="md:w-3/5 px-6 pb-8 md:pt-2 flex flex-col gap-5">
            {/* Title */}
            <div>
              <p className="text-[10px] font-bold tracking-widest uppercase text-stone-400 mb-1">{product.category}</p>
              <h2 className="text-xl font-black text-stone-900 leading-snug mb-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
                {product.name}
              </h2>
              <p className="text-sm text-stone-500 leading-relaxed">{product.description}</p>
            </div>

            {/* Rating */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-0.5">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="w-3.5 h-3.5 flex items-center justify-center">
                    <i className={`${i < Math.floor(product.rating) ? "ri-star-fill" : "ri-star-line"} text-amber-400 text-xs`} />
                  </div>
                ))}
              </div>
              <span className="text-sm font-semibold text-stone-700">{product.rating}</span>
              <span className="text-xs text-stone-400">({product.reviews}개 리뷰)</span>
            </div>

            {/* Features */}
            {features.length > 0 && (
              <div>
                <p className="text-xs font-bold text-stone-700 tracking-wider uppercase mb-2">주요 기능</p>
                <div className="flex flex-wrap gap-2">
                  {features.map((f) => (
                    <div
                      key={f.label}
                      className="flex items-center gap-1.5 bg-stone-50 border border-stone-100 rounded-full px-3 py-1.5"
                    >
                      <div className="w-3.5 h-3.5 flex items-center justify-center">
                        <i className={`${f.icon} text-stone-600 text-xs`} />
                      </div>
                      <span className="text-xs font-medium text-stone-700">{f.label}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Specs */}
            {specs.length > 0 && (
              <div>
                <p className="text-xs font-bold text-stone-700 tracking-wider uppercase mb-2">제품 사양</p>
                <div className="rounded-xl border border-stone-100 overflow-hidden">
                  {specs.map((s, i) => (
                    <div
                      key={s.label}
                      className={`flex items-center justify-between px-4 py-2.5 text-xs ${i % 2 === 0 ? "bg-stone-50" : "bg-white"}`}
                    >
                      <span className="text-stone-500 font-medium">{s.label}</span>
                      <span className="text-stone-800 font-semibold">{s.value}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* CTA */}
            <div className="flex flex-col gap-2 mt-auto pt-2">
              <button
                onClick={handleInquiry}
                className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-bold tracking-wide transition-colors whitespace-nowrap cursor-pointer"
                style={{ backgroundColor: "#FEE500", color: "#3A1D1D" }}
              >
                <i className="ri-chat-smile-2-line" />
                카카오톡으로 무료 견적 받기
              </button>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={handleInquiry}
                  className="flex items-center justify-center gap-1.5 py-3 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line" />
                  전화 문의
                </button>
                <button
                  onClick={() => setShowReview(true)}
                  className="flex items-center justify-center gap-1.5 py-3 rounded-xl border border-stone-200 text-sm font-semibold text-stone-700 hover:bg-stone-50 transition-colors whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-edit-line" />
                  리뷰 작성
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>

      {showReview && (
        <ReviewWriteModal
          initialProduct={product.name}
          onClose={() => setShowReview(false)}
        />
      )}
    </div>
  );
};

export default ProductDetailModal;
